import {Button, PanelHeader, PanelHeaderBack} from "@vkontakte/vkui";
import {useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import React, {useEffect, useState} from 'react';
import {
    Icon24CupOutline,
    Icon28CupOutline,
    Icon28Users3Outline,
    Icon36CoinsStacks2Outline
} from '@vkontakte/icons';
import BackgroundLetters from "../../components/Background/Background";
import {useSnackbar} from "../../context/SnackbarContext";
import BonusStore from "../../store/bonus";
import {getTypeBadgeClasses} from "../../utils/badges";
import {parseLaunchParams} from "../../utils/launch_params";
import {SUBSCRIBE_TOURNAMENT_MODAL} from "../../routing/routing";
import {getSubscriptionStatus} from "../../services/subscription";
import {
    checkTournamentParticipation,
    getTournamentLeaders,
    getTournaments,
    joinTournament
} from '../../services/tournament';
import {getPlayerInfo} from "../../services/player";
import {TournamentHistoryButton} from "../Home/TournamentHistoryButton";
import {triggerTelegramHapticNotification} from "../../services/telegramWebApp";

// Панель турниров следует единому шаблону: фон, заголовок и ограниченный контентный контейнер.
export default function TournamentPanel() {
    const SUBSCRIPTION_TOURNAMENT_MODAL_SHOWN_KEY = "wordmania_subscription_tournament_modal_shown";
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();
    const {showErrorSnackbar} = useSnackbar();
    const [tournaments, setTournaments] = useState([]);
    const [participationStatus, setParticipationStatus] = useState({});
    const [isParticipationLoading, setIsParticipationLoading] = useState(false);
    const [hasUserId, setHasUserId] = useState(false);
    const [tournamentLeaders, setTournamentLeaders] = useState({});
    const [isJoining, setIsJoining] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [coins, setCoins] = useState(BonusStore.coins || 0);

    // handleBackButton возвращает пользователя на предыдущий экран или на главную.
    const handleBackButton = () => {
        return isFirstPage ? routeNavigator.push('/') : routeNavigator.back();
    };

    useEffect(() => {
        loadTournaments();
        loadPlayerBalance();
    }, []);

    // loadTournaments загружает турниры и запускает загрузку связанных данных.
    // silent нужен, чтобы обновлять данные без сброса контента и скролла.
    const loadTournaments = (options = {}) => {
        const {silent = false} = options;
        // Состояние загрузки нужно для скелетонов и корректного UX.
        if (!silent) {
            setIsLoading(true);
            setLoadError('');
        }
        getTournaments().then(resp => {
            const data = resp.data.response;
            if (data === null) {
                if (!silent) {
                    setIsLoading(false);
                }
                return;
            }

            const typeOrder = {
                'daily': 1,
                'weekly': 2,
                'monthly': 3,
            };

            const sortedData = [...data].sort((a, b) => {
                const orderA = typeOrder[a.tournament_type] || 99;
                const orderB = typeOrder[b.tournament_type] || 99;
                return orderA - orderB;
            });

            setTournaments(sortedData);

            const params = parseLaunchParams();
            const hasUser = Boolean(params.tg_user_id);
            setHasUserId(hasUser);
            if (hasUser) {
                setParticipationStatus(buildParticipationMap(sortedData, null));
                checkParticipationForTournaments(sortedData);
                loadTournamentLeaders(sortedData);
            } else {
                setParticipationStatus(buildParticipationMap(sortedData, false));
                setIsParticipationLoading(false);
            }
            if (!silent) {
                setIsLoading(false);
            }
        }).catch(err => {
            console.error('Failed to load tournaments:', err);
            if (!silent) {
                setLoadError('Failed to load tournaments. Please try again.');
                setIsLoading(false);
            }
        });
    };

    // buildParticipationMap формирует карту участия, чтобы скрыть кнопку до проверки.
    const buildParticipationMap = (tournaments, isParticipating = null) => {
        const statusMap = {};
        tournaments.forEach(tournament => {
            statusMap[tournament.id] = isParticipating;
        });
        return statusMap;
    };

    // checkParticipationForTournaments собирает статус участия для каждого турнира.
    const checkParticipationForTournaments = (tournaments) => {
        setIsParticipationLoading(true);
        const participationPromises = tournaments.map((tournament) => {
            return checkTournamentParticipation(tournament.id).then(resp => {
                return {
                    tournamentId: tournament.id,
                    isParticipating: resp.data.response.is_participating
                };
            }).catch(err => {
                console.error(`Failed to check participation for tournament ${tournament.id}:`, err);
                return {
                    tournamentId: tournament.id,
                    isParticipating: false
                };
            });
        });

        Promise.all(participationPromises).then(results => {
            const statusMap = {};
            results.forEach(result => {
                statusMap[result.tournamentId] = result.isParticipating;
            });

            setParticipationStatus(statusMap);
            setIsParticipationLoading(false);
        }).catch(err => {
            console.error('Failed to check tournament participation:', err);
            setIsParticipationLoading(false);
        });
    };

    // loadTournamentLeaders получает лидеров и позицию пользователя для турниров.
    const loadTournamentLeaders = (tournaments) => {
        const leadersPromises = tournaments.map((tournament) => {
            return getTournamentLeaders(tournament.id).then(resp => {
                return {
                    tournamentId: tournament.id,
                    leaders: resp.data.response.leaders || [],
                    userPosition: resp.data.response.user_position || null
                };
            }).catch(err => {
                console.error(`Failed to load leaders for tournament ${tournament.id}:`, err);
                return {
                    tournamentId: tournament.id,
                    leaders: [],
                    userPosition: null
                };
            });
        });

        Promise.all(leadersPromises).then(results => {
            const leadersMap = {};
            results.forEach(result => {
                leadersMap[result.tournamentId] = {
                    leaders: result.leaders,
                    userPosition: result.userPosition
                };
            });

            setTournamentLeaders(leadersMap);
        }).catch(err => {
            console.error('Failed to load tournament leaders:', err);
        });
    };

    // handleJoinTournament проверяет баланс и инициирует вступление в турнир.
    const handleJoinTournament = (tournamentId) => {
        const tournament = tournaments.find(t => t.id === tournamentId);
        if (!tournament) return;

        if (BonusStore.coins < tournament.membership_fee) {
            showErrorSnackbar('Not enough coins');
            return;
        }

        setIsJoining(tournamentId);
        joinTournament(tournamentId).then(resp => {
            triggerTelegramHapticNotification("success");

            setParticipationStatus(prev => ({
                ...prev,
                [tournamentId]: true
            }));

            BonusStore.credit(tournament.membership_fee);
            setCoins(BonusStore.coins);

            // Обновляем данные без сброса списка, чтобы не прыгал скролл.
            loadTournaments({silent: true});

            // Показываем модалку подписки только тем, кто ещё не подписан, и один раз за сессию
            if (sessionStorage.getItem(SUBSCRIPTION_TOURNAMENT_MODAL_SHOWN_KEY) !== "1") {
                getSubscriptionStatus().then(response => {
                    if (!response?.data?.response?.subscribed) {
                        sessionStorage.setItem(SUBSCRIPTION_TOURNAMENT_MODAL_SHOWN_KEY, "1");
                        routeNavigator.showModal(SUBSCRIBE_TOURNAMENT_MODAL);
                    }
                }).catch(err => {
                    console.error("Failed to check subscription status after joining tournament:", err);
                });
            }
        }).catch(err => {
            console.error('Failed to join tournament:', err.response?.data?.response);
            triggerTelegramHapticNotification("error");
        }).finally(() => {
            setIsJoining(null);
        });
    };

    // getTournamentTitle нормализует заголовок турнира по типу.
    const getTournamentTitle = (type) => {
        switch (type) {
            case 'daily':
                return 'Daily tournament';
            case 'weekly':
                return 'Weekly tournament';
            case 'monthly':
                return 'Monthly tournament';
            default:
                return 'Tournament';
        }
    };

    const getTournamentDescription = (type) => {
        switch (type) {
            case 'daily':
                return 'Compete every day';
            case 'weekly':
                return 'Weekly competition';
            case 'monthly':
                return 'Main tournament of the month';
            default:
                return 'Tournament';
        }
    };

    const calculateTimeLeft = (endAt) => {
        const now = new Date();
        const end = new Date(endAt);
        const diff = end - now;

        if (diff <= 0) return 'Finished';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    // Загружает актуальный баланс монет вместе с первоначальной загрузкой данных
    const loadPlayerBalance = () => {
        getPlayerInfo().then(resp => {
            const player = resp.data.response;
            if (player?.coins !== undefined) {
                BonusStore.setCoins(player.coins);
                setCoins(player.coins);
            }
        }).catch(err => {
            console.error('Failed to load player balance:', err);
        });
    };

    return (
        <>
            <BackgroundLetters/>

            <PanelHeader
                className="sticky top-0 z-20"
                before={<PanelHeaderBack className={"[&>svg]:p-0"} onClick={handleBackButton}/>}
                separator={null}
            >
                Tournaments
            </PanelHeader>

            {/* Контейнер повторяет ограничение ширины max-w-lg, как на других панелях */}
            <div className="relative z-10 flex flex-col p-4 items-center mx-auto w-full max-w-lg">
                <div className="w-full">

                    <div className="w-full bg-gray-800 text-gray-400 rounded-2xl shadow-md p-5 space-y-6">
                        {/* Шапка */}
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-yellow-400 text-yellow-800 flex items-center justify-center">
                                <Icon28CupOutline/>
                            </div>
                            <div className={"text-gray-400"}>
                                <p className="text-base font-semibold">Active tournaments</p>
                                <p className="text-sm">Compete for prizes daily, weekly, and monthly</p>
                            </div>
                        </div>

                        {/* Список турниров */}
                        <div className={"flex flex-col gap-3"}>
                            {loadError ? (
                                <div className={"text-center text-gray-400 py-4 px-4 space-y-3"}>
                                    <p>{loadError}</p>
                                    <Button size="s" mode="secondary" onClick={loadTournaments}>
                                        Refresh
                                    </Button>
                                </div>
                            ) : isLoading ? (
                                <div className={"space-y-3"}>
                                    {[1, 2, 3].map((item) => (
                                        <div key={item}
                                             className="bg-gray-700 text-gray-400 rounded-xl p-4 shadow-sm border border-gray-800 animate-pulse">
                                            <div className="h-4 w-2/3 bg-gray-600 rounded mb-3"/>
                                            <div className="h-3 w-1/2 bg-gray-600 rounded mb-4"/>
                                            <div className="h-8 w-full bg-gray-600 rounded"/>
                                        </div>
                                    ))}
                                </div>
                            ) : tournaments.length === 0 ? (
                                <div className={"text-center text-gray-400 py-4 px-4"}>
                                    No active tournaments yet, but new ones are coming soon.
                                </div>
                            ) : (
                                <div className={'flex flex-col gap-3'}>
                                    {tournaments.map((tournament) => (
                                        <TournamentCard
                                            key={tournament.id}
                                            tournament={{
                                                ...tournament,
                                                title: getTournamentTitle(tournament.tournament_type),
                                                description: getTournamentDescription(tournament.tournament_type),
                                                type: tournament.tournament_type,
                                                membershipFee: tournament.membership_fee,
                                                reward: tournament.reward?.places?.map(p => p.coins) || [],
                                                rewardPlaces: tournament.reward?.places || [],
                                                timeLeft: calculateTimeLeft(tournament.end_at),
                                                participants: tournament.participants_count
                                            }}
                                            participationStatusValue={participationStatus[tournament.id]}
                                            shouldCheckParticipation={hasUserId}
                                            isParticipationLoading={isParticipationLoading}
                                            leaders={tournamentLeaders[tournament.id]?.leaders || []}
                                            userPosition={tournamentLeaders[tournament.id]?.userPosition || null}
                                            onJoin={() => handleJoinTournament(tournament.id)}
                                            isJoining={isJoining === tournament.id}
                                            coins={coins}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className={"px-4 mt-2"}>
                                <TournamentHistoryButton/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// TournamentCard - карточка отдельного турнира
function TournamentCard({
    tournament,
    participationStatusValue,
    shouldCheckParticipation,
    isParticipationLoading,
    leaders,
    userPosition,
    onJoin,
    isJoining,
    coins
}) {
    // Локальное раскрытие списка лидеров нужно, чтобы не перегружать карточку.
    const [showAllLeaders, setShowAllLeaders] = useState(false);

    // Позиция пользователя может приходить с разными ключами, делаем безопасный fallback.
    const userPositionValue = userPosition?.position ?? userPosition?.place ?? userPosition?.rank ?? null;
    const isNotEnoughCoins = coins < tournament.membershipFee;
    const isParticipationKnown = participationStatusValue !== null && participationStatusValue !== undefined;
    const isParticipating = Boolean(participationStatusValue);
    const rewardTotal = tournament.reward.reduce((sum, coins) => sum + coins, 0);
    const leadersToShow = showAllLeaders ? leaders : leaders.slice(0, 3);

    return (
        <div className={"bg-gray-700 text-gray-400 rounded-xl p-4 shadow-sm border border-gray-800"}>
            <div className={"flex justify-between items-start mb-2"}>
                <div className={"flex flex-col w-full"}>
                    <div className={"flex items-center gap-2 mb-1 overflow-hidden"}>
                        <Icon24CupOutline className={"text-yellow-500 flex-shrink-0"} width={20} height={20}/>
                        <span
                            className={"font-semibold text-sm whitespace-nowrap truncate min-w-0 text-gray-100"}>{tournament.title}</span>
                        <span
                            className={`${getTypeBadgeClasses(tournament.type)} ml-auto`}>
                            {tournament.timeLeft}
                        </span>
                    </div>
                    <p className={"text-xs text-gray-400"}>{tournament.description}</p>
                </div>
            </div>

            <div className={"flex justify-between items-center mb-3"}>
                <div className={"flex flex-col items-start gap-1 text-xs text-gray-400"}>
                    <div className={"flex items-center gap-1"}>
                        <Icon28Users3Outline width={16} height={16}/>
                        <span>Players:</span>
                        <span>{tournament.participants}</span>
                    </div>
                    <div className={"flex items-center gap-1"}>
                        <Icon36CoinsStacks2Outline width={16} height={16} className={"text-yellow-500"}/>
                        <span>Entry fee:</span>
                        <span>{tournament.membershipFee}</span>
                    </div>
                    <div className={"flex items-center gap-1"}>
                        <Icon36CoinsStacks2Outline width={16} height={16} className={"text-yellow-500"}/>
                        <span>Prize pool:</span>
                        <span className="text-yellow-600">{rewardTotal}</span>
                    </div>
                </div>
            </div>

            <div className={"flex flex-col mb-3"}>
                <span className={"text-xs text-gray-300 font-bold mb-1"}>Prizes:</span>
                <div className={"flex flex-col md:flex-row md:gap-4"}>
                    {tournament.reward.map((coins, index) => (
                        <div key={index} className={"flex items-center flex-row text-xs mb-1 text-gray-400"}>
                            <span className={"mr-1"}>{index + 1} place:</span>
                            <span className={"text-yellow-600 mr-1"}>{coins}</span>
                            <Icon36CoinsStacks2Outline width={14} height={14} className={"text-yellow-500"}/>
                        </div>
                    ))}
                </div>
            </div>

            {leaders.length > 0 && (
                <div className={"mb-3"}>
                    <span className={"text-xs text-gray-300 font-bold mb-2 block"}>Leaders:</span>
                    <div className={"space-y-1"}>
                        {leadersToShow.map((leader, index) => (
                            <div key={leader.user_id}
                                 className={"flex items-center justify-between text-xs text-gray-400 py-0.5"}>
                                {/* Очки показываем под именем, чтобы колонка выглядела ровно */}
                                <div className={"flex items-center gap-2 min-w-0"}>
                                    <span className={"font-semibold text-gray-200"}>{index + 1}.</span>
                                    <img
                                        src={leader.player_avatar}
                                        alt={leader.player_name}
                                        className="w-5 h-5 rounded-full"
                                    />
                                    <div className="flex flex-col min-w-0 leading-tight self-center">
                                        <span className="truncate max-w-24 text-gray-200">{leader.player_name}</span>
                                        <span className="font-normal text-gray-400 mt-0.5">
                                            {leader.score} {leader.score === 1 ? 'point' : 'points'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {tournament.rewardPlaces?.[index] && (
                                        <div className="flex items-center gap-1">
                                            <span
                                                className="text-yellow-600 text-xs">+{tournament.rewardPlaces[index].coins}</span>
                                            <Icon36CoinsStacks2Outline width={12} height={12}
                                                                       className="text-yellow-500"/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {leaders.length > 3 && (
                        <Button
                            size="s"
                            mode="secondary"
                            className="mt-2"
                            onClick={() => setShowAllLeaders(prev => !prev)}
                        >
                            {showAllLeaders ? 'Hide' : 'Show all'}
                        </Button>
                    )}
                </div>
            )}

            <div className={"flex flex-col justify-between items-center text-xs text-gray-400"}>
                {shouldCheckParticipation && !isParticipationKnown ? (
                    <div className={"w-full text-center mt-2 p-2 rounded rounded-xl border border-gray-600"}>
                        {isParticipationLoading ? 'Checking participation...' : 'Loading participation...'}
                    </div>
                ) : isParticipating ? (
                    <>
                        <div className={"w-full text-center mt-2 p-2 rounded rounded-xl border border-gray-600"}>
                            <span>You are participating in this tournament</span>
                        </div>

                        {/* Позиция пользователя, если он не в топ-3 */}
                        {userPositionValue && (
                            <div className={"mt-2"}>
                                <span>
                                    Your position: {userPositionValue}, {userPosition.score} {userPosition.score === 1 ? 'point' : 'points'}
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <Button
                        size="s"
                        mode="primary"
                        stretched
                        onClick={onJoin}
                        disabled={isJoining || isNotEnoughCoins}
                        className={"bg-yellow-400 hover:bg-yellow-500 text-gray-800 border-yellow-400"}
                    >
                        {isJoining ? 'Joining...' : isNotEnoughCoins ? 'Not enough coins' : 'Join'}
                    </Button>
                )}
            </div>
        </div>
    );
}
